import { AnchorIcon, FlameIcon, CompassIcon } from 'lucide-react'
import type { PillarPageContent } from '../../types/pillarPage'

export const espirituPage: PillarPageContent = {
  slug: 'espíritu',
  accent: { base: '198 162 74', deep: '51 40 15', ink: '8 9 9' },
  affiliateUrl: '',

  hero: {
    eyebrow: 'Espíritu · CreceElite',
    headlineTop: 'Tu propósito no comienza en tus planes.',
    headlineAccent: 'Comienza en tu fundamento.',
    quote:
      '“Tu propósito se fortalece cuando tu relación con Dios se vuelve tu fundamento.”',
    ctaLabel: 'Fortalecer mi fundamento',
    tags: ['Identidad', 'Oración', 'Disciplina espiritual', 'Propósito'],
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/4034d430-5884-4a95-8d1f-c716f455845c.jpg',
    imageAlt: 'Persona de pie en silencio dentro de un gran salón oscuro iluminado desde lo alto',
  },

  perspective: {
    eyebrow: 'Cambio de perspectiva',
    titleTop: 'No se trata de creer más.',
    titleSub: 'Se trata de vivir desde una raíz firme.',
    paragraphs: [
      'Puedes construir metas, empresas y hábitos impecables. Pero si tu vida interior está vacía, todo lo que edifiques descansará sobre algo que no sostiene.',
      'Tu espíritu define desde dónde vives: desde el miedo o desde la confianza, desde la prisa o desde la dirección, desde la aprobación de otros o desde tu identidad.',
      'Por eso, en CreceElite no vemos el crecimiento espiritual como religiosidad ni como una lista de reglas.',
    ],
    closingParagraph: 'Lo vemos como una forma de administración.',
    highlight: ['Cuida tu vida interior.', 'Fortalece tu identidad.', 'Camina con propósito.'],
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/74c68a0d-af61-433e-a3e7-4672c6f4a5d3.jpg',
    imageAlt: 'Libro abierto junto a una vela sobre una mesa de madera oscura',
    caption: 'Fundamento, no religiosidad',
  },

  foundation: {
    eyebrow: 'El fundamento',
    title: 'Tu vida interior merece atención porque sostiene todo lo demás.',
    quote:
      '“Buscad primeramente el reino de Dios y su justicia, y todas estas cosas os serán añadidas.”',
    quoteSource: 'Mateo 6:33',
    lead: 'El objetivo no es aparentar una vida espiritual perfecta.',
    closing:
      'El objetivo es desarrollar una relación real con Dios, capaz de sostenerte en la misión que puso delante de ti.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/e39bf9e7-319b-4d6c-9150-6aa7585f0ae2.jpg',
    imageAlt: 'Salón de piedra oscuro con un haz de luz cálida entrando por una ventana alta',
    virtues: [
      {
        label: 'Identidad',
        text: 'Saber quién eres antes de intentar demostrar lo que puedes hacer.',
        icon: AnchorIcon,
      },
      {
        label: 'Intimidad',
        text: 'Sostener una relación constante y honesta con Dios, no solo en la urgencia.',
        icon: FlameIcon,
      },
      {
        label: 'Dirección',
        text: 'Aprender a discernir el siguiente paso en lugar de avanzar por impulso.',
        icon: CompassIcon,
      },
    ],
  },

  pillars: {
    eyebrow: 'Los 3 pilares',
    title: 'Una vida espiritual sólida se construye sobre tres fundamentos.',
    items: [
      {
        number: '01',
        label: 'Intimidad',
        headline: 'La relación viene antes que la actividad.',
        text: 'La oración y el silencio no son un trámite religioso. Son el espacio donde se ordena tu interior, se calma la ansiedad y recuperas la perspectiva que el ruido te quita.',
      },
      {
        number: '02',
        label: 'Verdad',
        headline: 'Lo que crees sobre Dios define cómo te ves a ti mismo.',
        text: 'Alimentar tu mente con la Palabra cambia el criterio con el que interpretas tu vida. Deja de decidir desde lo que sientes y comienza a decidir desde lo que es verdad.',
      },
      {
        number: '03',
        label: 'Obediencia',
        headline: 'La fe se demuestra en decisiones concretas.',
        text: 'La transformación espiritual no ocurre solamente cuando entiendes algo nuevo, sino cuando lo obedeces. Dar el paso, aunque sea pequeño, es lo que forma carácter.',
      },
    ],
  },

  purpose: {
    eyebrow: 'El verdadero objetivo',
    title: '¿Para qué quieres una vida espiritual fuerte?',
    reasons: [
      { label: 'Para tener paz', text: 'Para sostenerte cuando todo alrededor se mueve.' },
      { label: 'Para tu familia', text: 'Para dejar un fundamento y no solamente un recuerdo.' },
      { label: 'Para tu trabajo', text: 'Para construir con integridad y no solo por resultados.' },
      {
        label: 'Para tu futuro',
        text: 'Para caminar con dirección en lugar de avanzar sin rumbo.',
      },
    ],
    statementLead: 'No buscas a Dios solamente para sentirte mejor.',
    statementAccent: 'Lo buscas para vivir con propósito.',
  },

  discipline: {
    eyebrow: 'Disciplina espiritual',
    titleTop: 'La emoción te acerca.',
    titleAccent: 'La constancia te transforma.',
    paragraphs: [
      'Habrá días en los que no sentirás nada.',
      'Días en los que orar, leer o esperar te parecerá inútil.',
    ],
    closingParagraph:
      'Pero la transformación ocurre cuando tu fe deja de depender de cómo te sientes y comienza a sostenerse en quién es Dios.',
    sequence: ['Encuentro', 'Verdad', 'Obediencia', 'Propósito'],
    closingLead: 'No necesitas una fe perfecta.',
    closingAccent: 'Necesitas una fe constante.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/fa4797a3-e4bd-4c2a-93f4-cd1a223119f2.jpg',
  },

  path: {
    eyebrow: 'Ahora necesitas un camino',
    title: 'Ahora necesitas un camino.',
    paragraphs: [
      'Querer una relación más profunda con Dios es solamente el comienzo.',
      'El siguiente paso es aprender qué hacer, cómo hacerlo y cómo mantenerlo en el tiempo.',
    ],
    steps: [
      { label: 'Oración', text: 'Aprende a sostener una vida de oración real y constante.' },
      { label: 'Palabra', text: 'Aprende a estudiar la Biblia y aplicarla a tus decisiones.' },
      { label: 'Identidad', text: 'Descubre quién eres desde la perspectiva de Dios.' },
      { label: 'Carácter', text: 'Desarrolla integridad, dominio propio y humildad.' },
      { label: 'Propósito', text: 'Convierte tu fe en una dirección clara para tu vida.' },
    ],
  },

  formation: {
    eyebrow: 'El siguiente paso',
    title: 'No tienes que descubrirlo todo solo.',
    paragraphs: [
      'Una de las mejores decisiones que puedes tomar es aprender de personas que ya han recorrido este camino.',
      'Por eso, hemos seleccionado una formación que puede ayudarte a fortalecer tu vida interior, desarrollar disciplina espiritual y caminar con mayor claridad de propósito.',
    ],
    cardEyebrow: 'Formación recomendada',
    cardTitle: 'Desarrolla tu espíritu',
    cardQuote: '“Busca. Escucha. Obedece. Repite.”',
    highlights: ['Busca.', 'Escucha.', 'Obedece.', 'Repite.'],
    ctaLabel: 'Conocer el método',
    note: 'Una recomendación de CreceElite para quienes buscan fundamento, no fórmulas.',
  },

  identity: {
    eyebrow: 'Identidad',
    title: '¿Quién estás decidiendo ser?',
    before: [
      'Vacío interior',
      'Ansiedad constante',
      'Fe intermitente',
      'Buscar aprobación',
      'Depender de las emociones',
    ],
    after: ['Paz', 'Identidad firme', 'Constancia', 'Integridad', 'Dirección'],
    statementLead: 'La transformación espiritual comienza mucho antes de que cambie tu vida.',
    statementAccent: 'Comienza cuando cambia tu fundamento.',
  },

  manifesto: {
    eyebrow: 'Manifiesto CreceElite',
    title: 'Mi vida interior es mi responsabilidad',
    lines: [
      'No busco religión. Busco relación.',
      'No busco perfección. Busco obediencia.',
      'No dependo de mis emociones. Me sostengo en la verdad.',
      'No corro sin dirección. Camino con propósito.',
      'No busco solamente ser bendecido. Quiero ser fiel.',
      'Porque todavía tengo mucho por vivir. Mucho por construir. Mucho por entregar.',
    ],
  },

  finalCta: {
    headlineTop: 'Tu espíritu es el fundamento.',
    headlineAccent: 'Tu propósito es el destino.',
    paragraph:
      'Ahora que entiendes el peso que tiene tu vida interior, el siguiente paso es desarrollar una disciplina espiritual que puedas sostener en el tiempo.',
    ctaLabel: 'Comenzar mi camino',
    subnote: 'Accede a la formación recomendada por CreceElite.',
  },

  stickyCtaLabel: 'Comenzar mi camino',
}
